import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useState } from "react";
import { z } from "zod";
import { Button } from "components/ui/button/Button";
import Form from "components/ui/form/Form";
import { FormInput } from "components/ui/form/FormInput";
import { Select } from "components/ui/select/Select";
import PhoneNumberInput from "components/ui/PhoneNumberInput/PhoneNumberInput";
import Footer from "components/footer/Footer";
import { emailAddress, firstname, lastname } from "utilities/formSchemas";
import useDesktopChecker from "hooks/useDesktopChecker";
import "./selfRegistration.scss";
import { Consent } from "components/consent/Consent";
import * as Constant from "utilities/Constant";
import LoanOfficerCardV2 from "components/v2/loan-officer-card/LoanOfficerCardV2";
import LoanOfficerCard from "components/v2/loan-officer-card/LoanOfficerCard";
import { useGetLoanOfficerByLoIdQuery } from "pages/application/applicationService";
import homeGraphic from "assets/images/HomeGraphic.png";
import {
    SelfRegisterRequest,
    usePostSelfRegistrationMutation,
} from "pages/registration/registrationService";
import { LoadingOverlay } from "components/ui/loading-spinners";
import ThankYou from "pages/thank-you/ThankYou";

const LOAN_PURPOSE_OPTIONS = ["Purchase", "Refinance"];

const formSchema = z.object({
    firstName: firstname,
    lastName: lastname,
    email: emailAddress,
    phone: z
        .string({ required_error: "Please enter phone number" })
        .min(1, { message: "Please enter phone number" })
        .refine((val) => val.replace(/\D/g, "").length === 10, {
            message: "Please enter a valid phone number",
        }),
    loanPurpose: z
        .string({ required_error: "Please select loan purpose" })
        .min(1, { message: "Please select loan purpose" }),
    subjectPropertyState: z
        .string({ required_error: "Please select subject property state" })
        .min(1, { message: "Please select subject property state" }),
});

type SelfRegistrationFormValues = z.infer<typeof formSchema>;

export default function SelfRegistration() {
    const [searchParams] = useSearchParams();
    const [error, setError] = useState(0);
    const [showLoading, setShowLoading] = useState(false);
    const isDesktop = useDesktopChecker();
    const stateOptions = Constant.US_STATES;
    const sourceCodeId = searchParams.get("src") || null;
    const loanOfficerId = searchParams.get("log") || null;

    const {
        handleSubmit,
        control,
        register,
        formState: { errors, isValid },
    } = useForm<SelfRegistrationFormValues>({
        resolver: zodResolver(formSchema),
        mode: "onChange",
    });

    const [sendRequest] = usePostSelfRegistrationMutation();
    const { data: loData } = useGetLoanOfficerByLoIdQuery(loanOfficerId ?? "", { skip: !loanOfficerId });
    const loanOfficer = loData?.fullName && loData.fullName !== "" && loData.fullName !== "Call Queue" ? loData : null;

    const onSubmit = (data: SelfRegisterRequest) => {
        setShowLoading(true);

        sendRequest(data)
            .unwrap()
            .then((redirectUrl: string) => {
                if (!redirectUrl || redirectUrl.trim().length === 0) {
                    setShowLoading(false);
                    setError(400);
                    return;
                }

                const cleanUrl = redirectUrl.trim();

                // Validate URL format - backend guarantees https:// protocol
                try {
                    new URL(cleanUrl);
                } catch {
                    // URL constructor throws TypeError for invalid URLs
                    setShowLoading(false);
                    setError(400);
                    return;
                }

                setTimeout(() => {
                    try {
                        globalThis.location.assign(cleanUrl);
                    } catch {
                        // Fallback to href if assign method fails
                        globalThis.location.href = cleanUrl;
                    }
                }, 100);
            })
            .catch((error_: { status?: number; data?: unknown; error?: unknown }) => {
                setShowLoading(false);

                if (error_.error && !error_.status) {
                    // Network/connectivity error without status code
                    setError(500);
                    return;
                }

                const status = error_.status;

                switch (status) {
                    case 400:
                    case 401:
                        setError(400);
                        break;
                    case 500:
                    default:
                        setError(500);
                        break;
                }
            });
    };

    const formContent = (
        <Form
            onSubmit={handleSubmit((data) => {
                onSubmit({
                    email: data.email,
                    firstName: data.firstName,
                    lastName: data.lastName,
                    phone: data.phone,
                    loanPurpose: data.loanPurpose,
                    subjectPropertyState: data.subjectPropertyState,
                    sourceCodeId: sourceCodeId ? Number.parseInt(sourceCodeId) : undefined,
                    loanOfficerId: loanOfficerId ?? undefined,
                    referringUrl: globalThis.location.href,
                    postUrl: globalThis.location.href,
                });
            })}
            formClassName="form self-registration__form-inner"
        >
            <div className="self-registration__grid">
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("firstName")}
                        inputProps={{
                            name: "firstName",
                            placeholder: "Enter first name",
                            label: "First Name",
                            errorMessage: errors.firstName?.message,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("lastName")}
                        inputProps={{
                            name: "lastName",
                            placeholder: "Enter last name",
                            label: "Last Name",
                            errorMessage: errors.lastName?.message,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <Controller
                        name="loanPurpose"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={LOAN_PURPOSE_OPTIONS}
                                placeholder="Select"
                                label="Loan Purpose"
                                errorMessage={errors.loanPurpose?.message}
                                onChange={onChange}
                                initialValue={value}
                            />
                        )}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <Controller
                        name="subjectPropertyState"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <Select
                                options={stateOptions}
                                placeholder="Select"
                                label="Subject Property State"
                                errorMessage={errors.subjectPropertyState?.message}
                                onChange={onChange}
                                initialValue={value}
                            />
                        )}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <FormInput
                        formHandle={register("email")}
                        inputProps={{
                            name: "email",
                            placeholder: "Enter email",
                            label: "Email Address",
                            errorMessage: errors.email?.message,
                        }}
                    />
                </div>
                <div className="self-registration__grid-item">
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <PhoneNumberInput
                                onChange={(newValue: string) => {
                                    onChange(newValue);
                                }}
                                value={value}
                                errorMessage={errors.phone?.message}
                                label="Phone Number"
                                placeholder="Enter phone number"
                            />
                        )}
                    />
                </div>
            </div>
            <Button
                title="Continue"
                className="self-registration__submit-btn"
                disabled={!isValid || showLoading}
            />
        </Form>
    );
    if (showLoading)
        return (
            <LoadingOverlay
                text="Setting up your profile"
                subtext="This will take a moment"
            />
        );
    if (error !== 0) {
        return <ThankYou />;
    }
    return (
        <>
            <div className="self-registration">
                <div className="self-registration__container">
                    <div className="self-registration__form">
                        <p className="self-registration__header">
                            We need a few details to get started
                        </p>
                        {loanOfficer && !isDesktop && (
                            <div className="self-registration__loan-officer-info">
                                <p className="self-registration__loan-officer-info-text">You've been invited to start your application.</p>
                                <LoanOfficerCard loInfo={loanOfficer} hideLabel />
                            </div>
                        )}
                        {formContent}
                        <div className="self-registration__consent">
                            <Consent text={Constant.PRIVACY_AND_TERMSOFUSE_CONSENT} />
                        </div>
                    </div>
                    {isDesktop && (
                        <div className="self-registration__loan-officer">
                            {loanOfficer ? (
                                <LoanOfficerCardV2 loInfo={loanOfficer} />
                            ) : (
                                <div className="self-registration__default-content">
                                    <div className="self-registration__message">
                                        <div className="self-registration__message-heading">
                                            <h1>Better Path to Homeownership</h1>
                                        </div>
                                        <div className="self-registration__message-sub-heading">
                                            <h2>Seamless, Secure, and Built for You</h2>
                                        </div>
                                        <ul className="self-registration__message-list">
                                            <li>Complete the questionnaire</li>
                                            <li>Upload required docs</li>
                                            <li>Talk to a loan officer</li>
                                        </ul>
                                    </div>
                                    <div className="self-registration__home-graphic-container">
                                        <img src={homeGraphic} alt="login-graphic" className="self-registration__home-graphic-container-image" />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="self-registration__footer-section">
                <Footer />
            </div>
        </>
    );
}
